'use client';

import { use, useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import mergeRefs from 'react-merge-refs';
import {
  Box,
  Button2,
  Dialog,
  DialogActions,
  DialogFooter,
  DialogHeader,
  Stack,
  TextareaField,
  TextField,
  theme,
  ToastAlert,
  useToast,
} from '@frameio/vapor';
import { useFeedbackDialogContext } from '../../contexts/ModalContext/ModalContext';
import { useSubmitFeedback } from '../../hooks/useSubmitFeedback';
import { APP_URL_PARAMS, UpdateAppParams } from '../../routing/params/params';
import TEXT from './generated/TEXT/FeedbackDialog';
import { GlobalFeedbackDialog } from './GlobalFeedbackDialog';

TEXT.DEFINE({
  /** Dialog title for generic feedback submission */
  genericTitle: 'Submit Feedback',
  /** Subtitle prompting user to share generic feedback */
  genericSubtitle:
    "We'd love to hear how Frame.io is working for you, and if there's anything we can do to improve your experience.",
  /** Success message when generic feedback is submitted */
  genericSuccessMessage: 'Feedback submitted',
  /** Error message when generic feedback submission fails */
  genericErrorMessage:
    "Sorry, your feedback wasn't submitted due to an error. Please contact us via the Contact Support button for help.",
  /** Dialog title for transcription feedback submission */
  transcriptionFeedbackTitle: 'Transcription Feedback',
  /** Subtitle prompting user to share transcription feedback */
  transcriptionFeedbackSubtitle:
    "We'd love to hear what you think about the transcription feature, and if there's anything we can do to improve your experience.",
  /** Success message when transcription feedback is submitted */
  transcriptionFeedbackSuccessMessage: 'Transcription feedback submitted',
  /** Error message when transcription feedback submission fails */
  transcriptionFeedbackErrorMessage:
    "Sorry, your transcription feedback wasn't submitted due to an error. Please contact us via the Contact Support button for help.",
  /** Dialog title for search feedback submission */
  searchFeedbackTitle: 'Share Feedback on Search',
  /** Subtitle prompting user to share search feedback */
  searchFeedbackSubtitle:
    "We'd love to hear what you think about search, and if there's anything we can do to improve your experience.",
  /** Success message when search feedback is submitted */
  searchFeedbackSuccessMessage: 'Search feedback submitted',
  /** Error message when search feedback submission fails */
  searchFeedbackErrorMessage:
    "Sorry, your search feedback wasn't submitted due to an error. Please contact us via the Contact Support button for help.",
  /** Dialog title for integrations feedback submission */
  integrationsFeedbackTitle: 'Submit Feedback',
  /** Subtitle prompting user to share Frame.io Panel feedback */
  integrationsFeedbackSubtitle:
    "We'd love to hear what you think about the Frame.io Panel, and if there's anything we can do to improve your experience.",
  /** Success message when integration feedback is submitted */
  integrationsFeedbackSuccessMessage: 'Frame.io V4 (Beta) feedback submitted',
  /** Error message when integration feedback submission fails */
  integrationsFeedbackErrorMessage:
    "Sorry, your Frame.io V4 (Beta) feedback wasn't submitted due to an error.",
  /** Dialog title for webhooks feedback submission */
  webhooksFeedbackTitle: 'Webhooks Feedback',
  /** Subtitle prompting user to share webhooks feedback */
  webhooksFeedbackSubtitle:
    "We'd love to hear what you think about webhooks, and if there's anything we can do to improve your experience.",
  /** Success message when webhooks feedback is submitted */
  webhooksFeedbackSuccessMessage: 'Webhooks feedback submitted',
  /** Error message when webhooks feedback submission fails */
  webhooksFeedbackErrorMessage:
    "Sorry, your webhooks feedback wasn't submitted due to an error. Please contact us via the Contact Support button for help.",
  /** Dialog title for actions feedback submission */
  actionsFeedbackTitle: 'Actions Feedback',
  /** Subtitle prompting user to share actions feedback */
  actionsFeedbackSubtitle:
    "We'd love to hear what you think actions, and if there's anything we can do to improve your experience.",
  /** Success message when actions feedback is submitted */
  actionsFeedbackSuccessMessage: 'Actions feedback submitted',
  /** Error message when actions feedback submission fails */
  actionsFeedbackErrorMessage:
    "Sorry, your actions feedback wasn't submitted due to an error. Please contact us via the Contact Support button for help.",
  /** Dialog title for chat feedback submission */
  chatFeedbackTitle: 'AI Assistant feedback',
  /** Subtitle prompting user to share AI Assistant feedback */
  chatFeedbackSubtitle:
    'We\u2019d love to hear what you think about the AI Assistant, and if there\u2019s anything we can do to improve your experience.',
  /** Success message when chat feedback is submitted */
  chatFeedbackSuccessMessage: 'AI Assistant feedback submitted',
  /** Error message when chat feedback submission fails */
  chatFeedbackErrorMessage:
    "Sorry, your AI Assistant feedback wasn't submitted due to an error. Please contact us via the Contact Support button for help.",
  /** Label for required summary field in feedback form */
  sharedSummaryRequired: 'Summary (required)',
  /** Label for required description field in feedback form */
  sharedDescriptionRequired: 'Description (required)',
  /** Button label to submit feedback form */
  sharedSubmitButton: 'Submit',
  /** Button label to cancel and close feedback dialog */
  sharedCancelButton: 'Cancel',
  /** Error message when summary field is empty */
  sharedSummaryError: 'Summary is required',
  /** Aria label for dialog close button */
  dialogCloseLabel: 'Close Dialog',
  /** Dialog title for drive feedback submission */
  driveFeedbackTitle: 'Submit feedback for Drive',
  /** Subtitle prompting user to share drive feedback feedback */
  driveFeedbackSubtitle:
    "We'd love to hear how Frame.io Drive is working for you, and if there's anything we can do to improve your experience.",
  /** Success message when drive feedback is submitted */
  driveFeedbackSuccessMessage: 'Drive feedback submitted',
  /** Error message when drive feedback submission fails */
  driveFeedbackErrorMessage:
    "Sorry, your Drive feedback wasn't submitted due to an error. Please contact us via the Contact Support button for help.",
  /** Dialog title for Firefly Actions feedback submission */
  fireflyActionsFeedbackTitle: 'Submit Firefly Actions Feedback',
  /** Subtitle prompting user to share Firefly Actions feedback */
  fireflyActionsFeedbackSubtitle:
    "We'd love to hear what you think about Firefly Actions, and if there's anything we can do to improve your experience.",
  /** Success message when Firefly Actions feedback is submitted */
  fireflyActionsFeedbackSuccessMessage: 'Firefly Actions feedback submitted',
  /** Error message when Firefly Actions feedback submission fails */
  fireflyActionsFeedbackErrorMessage:
    "Sorry, your Firefly Actions feedback wasn't submitted due to an error. Please contact us via the Contact Support button for help.",
});

type FeedbackDialogStrings = {
  title: string;
  subtitle: string;
  summaryLabel: string;
  summaryPlaceholder: string;
  descriptionLabel: string;
  descriptionPlaceholder: string;
  submitButton: string;
  cancelButton: string;
  successMessage: string;
  errorMessage: string;
};

/**
 * Feedback Dialog Variants
 *
 * Add new variants here and update the feedbackVariantLibrary object below.
 */
export type FeedbackDialogVariant =
  | 'generic'
  | 'featureRequest'
  | 'transcriptionFeedback'
  | 'searchFeedback'
  | 'integrationsFeedback'
  | 'webhooksFeedback'
  | 'actionsFeedback'
  | 'chatFeedback'
  | 'driveFeedback'
  | 'fireflyActionsFeedback';

type LegacyFeedbackDialogVariant = Exclude<
  FeedbackDialogVariant,
  'featureRequest'
>;

/**
 * Origination values are system metadata consumed by the feedback hub
 * automations and must NOT be localized.
 */
const ORIGINATION: Record<FeedbackDialogVariant, string> = {
  generic: 'Generic',
  featureRequest: 'Feature Request',
  transcriptionFeedback: 'Transcription',
  searchFeedback: 'Search',
  integrationsFeedback: 'Premiere Pro V4 Integration',
  webhooksFeedback: 'Webhooks',
  actionsFeedback: 'Actions',
  chatFeedback: 'Collaborator-Feedback-Form',
  driveFeedback: 'Drive',
  fireflyActionsFeedback: 'Firefly-Actions',
};

/**
 * Feedback Variant Library
 *
 * Returns localized strings for a given feedback variant.
 * This is a function (not a module-level constant) so that TEXT getters
 * are evaluated at render time, after the locale has been loaded.
 *
 * Add new variants to the type FeedbackDialogVariant above.
 * If no variant is provided, the generic variant will be used.
 */
function getFeedbackVariantStrings(): Record<
  LegacyFeedbackDialogVariant,
  FeedbackDialogStrings
> {
  const feedbackVariantLibrary: Record<
    LegacyFeedbackDialogVariant,
    FeedbackDialogStrings
  > = {
    generic: {
      title: TEXT.genericTitle,
      subtitle: TEXT.genericSubtitle,
      summaryLabel: TEXT.sharedSummaryRequired,
      summaryPlaceholder: '',
      descriptionLabel: TEXT.sharedDescriptionRequired,
      descriptionPlaceholder: '',
      submitButton: TEXT.sharedSubmitButton,
      cancelButton: TEXT.sharedCancelButton,
      successMessage: TEXT.genericSuccessMessage,
      errorMessage: TEXT.genericErrorMessage,
    },
    transcriptionFeedback: {
      title: TEXT.transcriptionFeedbackTitle,
      subtitle: TEXT.transcriptionFeedbackSubtitle,
      summaryLabel: TEXT.sharedSummaryRequired,
      summaryPlaceholder: '',
      descriptionLabel: TEXT.sharedDescriptionRequired,
      descriptionPlaceholder: '',
      submitButton: TEXT.sharedSubmitButton,
      cancelButton: TEXT.sharedCancelButton,
      successMessage: TEXT.genericSuccessMessage,
      errorMessage: TEXT.transcriptionFeedbackErrorMessage,
    },
    searchFeedback: {
      title: TEXT.searchFeedbackTitle,
      subtitle: TEXT.searchFeedbackSubtitle,
      summaryLabel: TEXT.sharedSummaryRequired,
      summaryPlaceholder: '',
      descriptionLabel: TEXT.sharedDescriptionRequired,
      descriptionPlaceholder: '',
      submitButton: TEXT.sharedSubmitButton,
      cancelButton: TEXT.sharedCancelButton,
      successMessage: TEXT.searchFeedbackSuccessMessage,
      errorMessage: TEXT.searchFeedbackErrorMessage,
    },
    integrationsFeedback: {
      title: TEXT.integrationsFeedbackTitle,
      subtitle: TEXT.integrationsFeedbackSubtitle,
      summaryLabel: TEXT.sharedSummaryRequired,
      summaryPlaceholder: '',
      descriptionLabel: TEXT.sharedDescriptionRequired,
      descriptionPlaceholder: '',
      submitButton: TEXT.sharedSubmitButton,
      cancelButton: TEXT.sharedCancelButton,
      successMessage: TEXT.integrationsFeedbackSuccessMessage,
      errorMessage: TEXT.integrationsFeedbackErrorMessage,
    },
    webhooksFeedback: {
      title: TEXT.webhooksFeedbackTitle,
      subtitle: TEXT.webhooksFeedbackSubtitle,
      summaryLabel: TEXT.sharedSummaryRequired,
      summaryPlaceholder: '',
      descriptionLabel: TEXT.sharedDescriptionRequired,
      descriptionPlaceholder: '',
      submitButton: TEXT.sharedSubmitButton,
      cancelButton: TEXT.sharedCancelButton,
      successMessage: TEXT.webhooksFeedbackSuccessMessage,
      errorMessage: TEXT.webhooksFeedbackErrorMessage,
    },
    actionsFeedback: {
      title: TEXT.actionsFeedbackTitle,
      subtitle: TEXT.actionsFeedbackSubtitle,
      summaryLabel: TEXT.sharedSummaryRequired,
      summaryPlaceholder: '',
      descriptionLabel: TEXT.sharedDescriptionRequired,
      descriptionPlaceholder: '',
      submitButton: TEXT.sharedSubmitButton,
      cancelButton: TEXT.sharedCancelButton,
      successMessage: TEXT.actionsFeedbackSuccessMessage,
      errorMessage: TEXT.actionsFeedbackErrorMessage,
    },
    chatFeedback: {
      title: TEXT.chatFeedbackTitle,
      subtitle: TEXT.chatFeedbackSubtitle,
      summaryLabel: TEXT.sharedSummaryRequired,
      summaryPlaceholder: '',
      descriptionLabel: TEXT.sharedDescriptionRequired,
      descriptionPlaceholder: '',
      submitButton: TEXT.sharedSubmitButton,
      cancelButton: TEXT.sharedCancelButton,
      successMessage: TEXT.chatFeedbackSuccessMessage,
      errorMessage: TEXT.chatFeedbackErrorMessage,
    },
    driveFeedback: {
      title: TEXT.driveFeedbackTitle,
      subtitle: TEXT.driveFeedbackSubtitle,
      summaryLabel: TEXT.sharedSummaryRequired,
      summaryPlaceholder: '',
      descriptionLabel: TEXT.sharedDescriptionRequired,
      descriptionPlaceholder: '',
      submitButton: TEXT.sharedSubmitButton,
      cancelButton: TEXT.sharedCancelButton,
      successMessage: TEXT.driveFeedbackSuccessMessage,
      errorMessage: TEXT.driveFeedbackErrorMessage,
    },
    fireflyActionsFeedback: {
      title: TEXT.fireflyActionsFeedbackTitle,
      subtitle: TEXT.fireflyActionsFeedbackSubtitle,
      summaryLabel: TEXT.sharedSummaryRequired,
      summaryPlaceholder: '',
      descriptionLabel: TEXT.sharedDescriptionRequired,
      descriptionPlaceholder: '',
      submitButton: TEXT.sharedSubmitButton,
      cancelButton: TEXT.sharedCancelButton,
      successMessage: TEXT.fireflyActionsFeedbackSuccessMessage,
      errorMessage: TEXT.fireflyActionsFeedbackErrorMessage,
    },
  };
  return feedbackVariantLibrary;
}

export const FeedbackDialog = ({
  additionalFormData,
}: {
  additionalFormData?: Record<string, string>;
}) => {
  const initialFocusRef = useRef(undefined);
  const variantRef = useRef<string | null>(null);
  const {
    isFeedbackDialogOpen,
    closeFeedbackDialog,
    variant,
    openFeedbackDialog,
  } = useFeedbackDialogContext();
  const hasOpenedFromUrlParamRef = useRef(false);
  const [, replaceState] = use(UpdateAppParams);
  const feedbackParam = use(APP_URL_PARAMS.feedback);

  /**
   * Opens a feedback modal variant automatically based on url param
   * ex: ?feedback=featureRequest
   */
  useEffect(() => {
    if (feedbackParam && !hasOpenedFromUrlParamRef.current) {
      hasOpenedFromUrlParamRef.current = true;
      openFeedbackDialog(feedbackParam as FeedbackDialogVariant);
    }
  }, [feedbackParam, openFeedbackDialog]);

  const { addToast } = useToast();
  const submitFeedback = useSubmitFeedback();
  const [isSubmitting, setIsSubmitting] = useState(false);

  /**
   * Get the text item for the feedback dialog based on the current variant.
   * @param item - The item to get the text for.
   * @returns The text item.
   */
  const feedbackVariantLibrary = getFeedbackVariantStrings();
  const allowedVariants = Object.keys(feedbackVariantLibrary);

  const getTextString = (item: keyof FeedbackDialogStrings) => {
    // Set the variantRef to prevent the text from changing as the modal animates out.
    if (isFeedbackDialogOpen && variant) {
      variantRef.current = variant;
    }
    // Use the variantRef if the variant is not set, like when the modal is closing.
    const effectiveVariant = isFeedbackDialogOpen
      ? variant
      : variantRef.current;
    if (!effectiveVariant || !allowedVariants.includes(effectiveVariant)) {
      return feedbackVariantLibrary.generic[item];
    }
    return feedbackVariantLibrary[
      effectiveVariant as LegacyFeedbackDialogVariant
    ][item];
  };

  const {
    register,
    handleSubmit,
    reset,
    formState: { isValid, errors },
  } = useForm({
    defaultValues: {
      summary: '',
      description: '',
    },
    mode: 'onChange',
  });

  const summaryField = register('summary', {
    required: true,
    validate: (value) => value.trim().length > 0,
  });

  const descriptionField = register('description', {
    required: true,
    validate: (value) => value.trim().length > 0,
  });

  const mergedSummaryRefs = mergeRefs([summaryField.ref, initialFocusRef]);

  const handleCloseDialog = () => {
    reset();
    setIsSubmitting(false);
    closeFeedbackDialog();
    replaceState({ feedback: null });
  };
  const submitFormData = async ({
    summary,
    description,
  }: {
    summary: string;
    description: string;
  }) => {
    setIsSubmitting(true);

    try {
      await submitFeedback({
        origination: ORIGINATION[variant ?? 'generic'] ?? ORIGINATION.generic,
        summary,
        description,
        additionalFormData: {
          ...(variantRef.current ? { variant: variantRef.current } : {}),
          ...additionalFormData,
        },
      });

      addToast(
        (closeToast, toastCloseLabel) => (
          <ToastAlert
            closeLabel={toastCloseLabel}
            onRequestClose={closeToast}
            title={getTextString('successMessage')}
            variant="success"
          />
        ),
        { duration: 5000 }
      );
      handleCloseDialog();
    } catch (error) {
      setIsSubmitting(false);
      addToast(
        (closeToast, toastCloseLabel) => (
          <ToastAlert
            closeLabel={toastCloseLabel}
            onRequestClose={closeToast}
            title={getTextString('errorMessage')}
            variant="error"
          />
        ),
        { duration: 5000 }
      );
    }
  };

  return (
    <>
      <Dialog
        aria-labelledby="Feedback-dialog"
        data-testid="feedback-dialog"
        enableClickOutsideToClose={variant === 'searchFeedback'}
        hasOutermostTheme={true}
        initialFocusRef={initialFocusRef}
        isShowing={isFeedbackDialogOpen && variant !== 'featureRequest'}
        onRequestClose={handleCloseDialog}
        size="medium"
        zIndex={theme.zIndices.modal + 1}
      >
        <form id="feedback-form" onSubmit={handleSubmit(submitFormData)}>
          <DialogHeader
            closeLabel={TEXT.dialogCloseLabel}
            subtitle={getTextString('subtitle')}
            title={getTextString('title')}
          />
          <Box padding={16}>
            <Stack gap={16}>
              <TextField
                {...summaryField}
                ref={mergedSummaryRefs}
                error={errors.summary ? TEXT.sharedSummaryError : ''}
                label={getTextString('summaryLabel')}
                maxLength={255}
                placeholder={getTextString('summaryPlaceholder')}
                required={true}
              />
              <TextareaField
                {...descriptionField}
                autosize={false}
                label={getTextString('descriptionLabel')}
                maxLength={5000}
                placeholder={getTextString('descriptionPlaceholder')}
                required={true}
                rowsMax={12}
                rowsMin={8}
              />
            </Stack>
          </Box>
          <DialogFooter>
            <DialogActions>
              <Button2 onPress={handleCloseDialog}>
                {getTextString('cancelButton')}
              </Button2>
              <Button2
                isDisabled={isSubmitting || !isValid}
                isLoading={isSubmitting}
                type="submit"
                variant="primary"
              >
                {getTextString('submitButton')}
              </Button2>
            </DialogActions>
          </DialogFooter>
        </form>
      </Dialog>
      <GlobalFeedbackDialog additionalFormData={additionalFormData} />
    </>
  );
};
